import { $api, invalidateTypedQueries } from '@/hooks/useApiClient'
import api from '@/utils/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isFunction } from 'lodash'
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react'

const parseValue = <T>(value: unknown, defaultValue: T) => {
  if (typeof value !== 'string' || value === 'undefined') return defaultValue
  try {
    return JSON.parse(value) as T
  } catch (_err) {
    return defaultValue
  }
}

export const useSetting = <T>(key: string, defaultValue: T) => {
  const { data, isLoading } = $api.useQuery('post', '/user/settings/get', {
    params: { query: { key } },
  })
  const client = useQueryClient()
  const { variables, isPending, mutate } = useMutation({
    mutationFn: (value: T) => {
      const formData = new FormData()
      formData.set('value', value as string)
      return api.updateUserSetting(key, formData)
    },
    onSettled: () => invalidateTypedQueries(client, 'post', '/user/settings/get', { params: { query: { key } } }),
  })
  const value = useMemo(() => {
    const rawValue = isPending ? variables : parseValue(data, defaultValue)
    return rawValue ?? defaultValue
  }, [data, isPending, variables, defaultValue])
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      const newValue = isFunction(action) ? action(value) : action
      mutate(newValue)
    },
    [key, mutate, value]
  )
  return [value, setValue, isLoading] as const
}
